import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Loader2, Plus, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useSlip } from "../context/SlipContext";
import { ConfidenceRing } from "./ConfidenceRing";
import { ProjectedPick } from "./ProjectedPick";
import { getBooksAgree } from "./MarketLean";

const SuggestionPick = ({ s, props, i }) => {
  const pr = props.find((p) => p.player_name === s.player_name && p.metric === s.metric);
  const ba = pr ? getBooksAgree({ ...pr, lean: s.lean }) : null;
  return (
    <ProjectedPick
      testId={`ai-suggestion-projection-${i}`}
      projection={pr?.projection}
      metric={s.metric}
      side={s.lean}
      line={pr?.line ?? s.line}
      odds={pr ? (s.lean === "OVER" ? pr.over_odds : pr.under_odds) : null}
      book={pr?.bookmaker}
      booksAgree={ba?.agree}
      booksTotal={ba?.total}
    />
  );
};

export const AISuggestPanel = ({ matchupId, props }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const { addLeg } = useSlip();

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.post("/ai/suggest", { matchup_id: matchupId });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI analysis failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const addSuggestion = (s) => {
    const prop = props.find(
      (p) => p.player_name === s.player_name && p.metric === s.metric
    );
    if (prop) addLeg(prop, s.lean);
    else toast.error("Prop not found on slate");
  };

  return (
    <div className="rounded-2xl border border-[#00E5FF]/30 bg-gradient-to-b from-[#0e2733] to-[#141C2B] p-5 relative overflow-hidden grain">
      <div className="relative z-10">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-[#00E5FF] grid place-items-center cyan-glow">
              <Sparkles className="h-4 w-4 text-[#0B0F17]" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-white text-base leading-tight">AI Suggested Props</h3>
              <p className="text-[11px] text-slate-400">Powered by Claude Sonnet 5.5 · stats + matchup model</p>
            </div>
          </div>
        </div>

        {!data && (
          <button
            data-testid="ai-suggest-props-button"
            onClick={run}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#00E5FF] text-[#0B0F17] font-semibold py-3 hover:bg-[#33EaFF] transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
            {loading ? "Analyzing matchup…" : "Generate AI Picks"}
          </button>
        )}

        {data && (
          <div className="space-y-3">
            {data.matchup_summary && (
              <p className="text-sm text-slate-300 leading-relaxed border-l-2 border-[#00E5FF] pl-3">
                {data.matchup_summary}
              </p>
            )}
            {(data.suggestions || []).map((s, i) => (
              <motion.div
                key={i}
                data-testid={`ai-suggestion-${i}`}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.08 }}
                className="rounded-xl border border-[#233248] bg-[#0B0F17]/60 p-3"
              >
                <div className="flex items-center gap-3">
                  <ConfidenceRing value={s.confidence} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-semibold text-sm truncate">{s.player_name}</span>
                      <span className="font-data text-[10px] text-slate-500">{s.team}</span>
                    </div>
                    <SuggestionPick s={s} props={props} i={i} />
                  </div>
                  <button
                    data-testid={`add-suggestion-${i}`}
                    onClick={() => addSuggestion(s)}
                    className="shrink-0 rounded-lg border border-[#233248] p-2 text-[#00E5FF] hover:bg-[#1B273E]"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                {s.reasoning && <p className="text-xs text-slate-400 mt-2 leading-relaxed">{s.reasoning}</p>}
                {s.key_drivers?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {s.key_drivers.map((d, j) => (
                      <span key={j} className="rounded-md bg-[#1B273E] px-2 py-0.5 text-[10px] text-slate-300">
                        {d}
                      </span>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
            <button
              onClick={run}
              disabled={loading}
              className="w-full text-xs text-slate-400 hover:text-white py-1"
            >
              {loading ? "Refreshing…" : "Regenerate"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
